import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "./axiosConfig";
import ChevronToggleButton from "./ChevronToggleButton";

// #region Constants: API base, competition options, and fixture status groups.
const COMPETITIONS = [
  { id: 39, label: "Premier League" },
  { id: 1, label: "World Cup" },
  { id: 2, label: "Champions League" },
];

const FINISHED_STATUSES = new Set(["FT", "FINISHED", "AET", "PEN"]);
const IN_PROGRESS_STATUSES = new Set([
  "IN_PLAY",
  "LIVE",
  "1H",
  "2H",
  "HT",
  "ET",
  "BT",
  "PAUSED",
]);
const BLOCKED_SCORE_STATUSES = new Set([
  "POSTPONED",
  "SUSPENDED",
  "CANCELLED",
  "PST",
  "CANC",
]);
// #endregion

// #region Helper Functions: Utility functions for sorting, status checks, and LOS team matching.
const getRoundSortValue = (roundLabel) => {
  const matches = String(roundLabel).match(/(\d+)/g);
  if (!matches || matches.length === 0) return Number.MAX_SAFE_INTEGER;
  return Number(matches[matches.length - 1]);
};

const isLeagueStyleRoundLabel = (roundLabel) =>
  /^matchday\s+\d+$/i.test(String(roundLabel || "").trim());

const shouldPreferRoundOrder = (gameweeks) => {
  if (!Array.isArray(gameweeks) || gameweeks.length === 0) return false;
  return gameweeks.every((gameweek) =>
    isLeagueStyleRoundLabel(String(gameweek?.id || "")),
  );
};

const isFixtureFinished = (status) =>
  FINISHED_STATUSES.has(String(status || ""));

const isFixtureInProgress = (status) =>
  IN_PROGRESS_STATUSES.has(String(status || ""));

const isFixtureBlockedForScore = (status) =>
  BLOCKED_SCORE_STATUSES.has(String(status || ""));

const getFixtureStatusLabel = (status) => {
  if (status === "TIMED") return "SCHEDULED";
  if (status === "FINISHED") return "FT";
  if (status === "POSTPONED" || status === "PST") return "POSTPONED";
  if (status === "CANCELLED" || status === "CANC") return "CANCELLED";
  if (status === "SUSPENDED") return "SUSPENDED";
  return status || "SCHEDULED";
};

const getFixtureStatusClass = (status) => {
  if (["FT", "FINISHED", "AET", "PEN"].includes(status)) {
    return "status-finished";
  }
  if (["IN_PLAY", "LIVE", "1H", "2H", "HT"].includes(status)) {
    return "status-live";
  }
  if (["POSTPONED", "SUSPENDED", "CANCELLED", "PST", "CANC"].includes(status)) {
    return "status-postponed";
  }
  return "status-scheduled";
};

const hasLiveFixtureInGameweek = (gameweek) => {
  const fixtures = gameweek?.fixtures || [];
  if (fixtures.length === 0) return false;

  return fixtures.some((fixture) => isFixtureInProgress(fixture.statusShort));
};

const hasRemainingPlayableFixture = (gameweek) => {
  const fixtures = gameweek?.fixtures || [];
  if (fixtures.length === 0) return false;

  return fixtures.some(
    (fixture) =>
      !isFixtureFinished(fixture.statusShort) &&
      !isFixtureBlockedForScore(fixture.statusShort),
  );
};

const resolveCurrentGameweekIndex = (sortedGameweeks) => {
  if (!Array.isArray(sortedGameweeks) || sortedGameweeks.length === 0) {
    return -1;
  }

  const now = Date.now();

  const liveIndex = sortedGameweeks.findIndex((gameweek) =>
    hasLiveFixtureInGameweek(gameweek),
  );
  if (liveIndex >= 0) return liveIndex;

  const remainingPlayableIndex = sortedGameweeks.findIndex((gameweek) =>
    hasRemainingPlayableFixture(gameweek),
  );
  if (remainingPlayableIndex >= 0) return remainingPlayableIndex;

  const nextIndex = sortedGameweeks.findIndex((gameweek) => {
    if (!gameweek.firstKickoff) return false;
    const kickoffTs = new Date(gameweek.firstKickoff).getTime();
    return Number.isFinite(kickoffTs) && kickoffTs > now;
  });

  if (nextIndex === -1) {
    return sortedGameweeks.length - 1;
  }

  if (nextIndex === 0) {
    return 0;
  }

  return nextIndex;
};

const getDefaultGameweekId = (sortedGameweeks) => {
  if (!sortedGameweeks.length) return "";
  const resolvedIndex = resolveCurrentGameweekIndex(sortedGameweeks);
  if (resolvedIndex < 0) return "";
  return sortedGameweeks[resolvedIndex]?.id || "";
};

const normalizeTeamName = (teamName) =>
  String(teamName || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\b(fc|afc|cf|sc)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const teamNamesMatch = (a, b) => {
  const normalizedA = normalizeTeamName(a);
  const normalizedB = normalizeTeamName(b);
  if (!normalizedA || !normalizedB) return false;
  return (
    normalizedA === normalizedB ||
    normalizedA.includes(normalizedB) ||
    normalizedB.includes(normalizedA)
  );
};

const renderTeamLabel = (name, crestUrl) => (
  <span className="fixture-team">
    {crestUrl && (
      <img
        className="team-crest"
        src={crestUrl}
        alt={`${name} crest`}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    )}
    <span>{name}</span>
  </span>
);

const isTeamAlreadyUsed = (teamName, usedTeamsForUser) =>
  usedTeamsForUser.some((usedTeam) => teamNamesMatch(teamName, usedTeam));
// #endregion

function Predictions({
  username,
  leaguesRefreshSignal = 0,
  activeCompetitionId = 39,
  onCompetitionChange,
}) {
  // #region State: Component state for leagues, predictions, LOS status, and UI toggles.
  const [leagues, setLeagues] = useState([]);
  const [isLoadingLeagues, setIsLoadingLeagues] = useState(true);
  const [leagueError, setLeagueError] = useState("");
  const [expandedLeagueIds, setExpandedLeagueIds] = useState({});

  const [gameweeks, setGameweeks] = useState([]);
  const [isLoadingGameweeks, setIsLoadingGameweeks] = useState(true);
  const [selectedGameweekId, setSelectedGameweekId] = useState("");
  // Tracks whether the initial gameweek selection has happened this mount.
  // After first selection, competition changes always use resolveCurrentGameweekIndex (no localStorage restore).
  const hasInitializedGameweek = useRef(false);

  const [leaderboardMode, setLeaderboardMode] = useState("season");
  const [activeScoreLeagueId, setActiveScoreLeagueId] = useState("");
  const [predictions, setPredictions] = useState({});
  const [fixturesWithScores, setFixturesWithScores] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTime, setLockTime] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [isSeasonComplete, setIsSeasonComplete] = useState(false);
  const [currentMatchdayPointsByUser, setCurrentMatchdayPointsByUser] =
    useState({});
  const [scoreErrorMessage, setScoreErrorMessage] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [showSubmissionFlash, setShowSubmissionFlash] = useState(false);
  const [areScoreFixturesExpanded, setAreScoreFixturesExpanded] =
    useState(true);

  const [losStatusByLeague, setLosStatusByLeague] = useState({});
  const [losErrorsByLeague, setLosErrorsByLeague] = useState({});
  const [losSavingByLeague, setLosSavingByLeague] = useState({});
  const [deletingLeagueById, setDeletingLeagueById] = useState({});
  const [leavingLeagueById, setLeavingLeagueById] = useState({});
  const [showScoreSectionRulesInfo, setShowScoreSectionRulesInfo] =
    useState(false);
  const [showLosSectionRulesInfo, setShowLosSectionRulesInfo] = useState(false);
  const [showLosActiveUsersByLeague, setShowLosActiveUsersByLeague] = useState(
    {},
  );
  const [showLosEliminatedByLeague, setShowLosEliminatedByLeague] = useState(
    {},
  );
  const [scoreLeagueUserStandingByLeague, setScoreLeagueUserStandingByLeague] =
    useState({});
  const [losSelectedGameweekByLeague, setLosSelectedGameweekByLeague] =
    useState({});
  const [losPickByLeague, setLosPickByLeague] = useState({});
  const [expandedLeaderboards, setExpandedLeaderboards] = useState({});
  const [leaderboardPages, setLeaderboardPages] = useState({});
  const [losSelectionByLeague, setLosSelectionByLeague] = useState({});
  const [losFlashByLeague, setLosFlashByLeague] = useState({});
  const [losRestartingByLeague, setLosRestartingByLeague] = useState({});
  const [losActionMessageByLeague, setLosActionMessageByLeague] = useState({});
  // #endregion

  // #region Derived Values: Computed gameweek context, league subsets, and active fixture lists.
  const sortedGameweeks = useMemo(() => {
    const preferRoundOrder = shouldPreferRoundOrder(gameweeks);
    return [...gameweeks].sort((a, b) => {
      const aRound = getRoundSortValue(a.id);
      const bRound = getRoundSortValue(b.id);
      const aTime = a.firstKickoff
        ? new Date(a.firstKickoff).getTime()
        : Number.MAX_SAFE_INTEGER;
      const bTime = b.firstKickoff
        ? new Date(b.firstKickoff).getTime()
        : Number.MAX_SAFE_INTEGER;

      if (preferRoundOrder) {
        if (aRound !== bRound) return aRound - bRound;
        if (aTime !== bTime) return aTime - bTime;
        return String(a.id).localeCompare(String(b.id));
      }

      if (aTime !== bTime) return aTime - bTime;
      if (aRound !== bRound) return aRound - bRound;

      return String(a.id).localeCompare(String(b.id));
    });
  }, [gameweeks]);

  const currentGameweekId = useMemo(
    () => getDefaultGameweekId(sortedGameweeks),
    [sortedGameweeks],
  );

  const isScoreSeasonActive = useMemo(
    () =>
      sortedGameweeks.some((gw) =>
        (gw.fixtures || []).some(
          (fixture) =>
            !isFixtureFinished(fixture.statusShort) &&
            !isFixtureBlockedForScore(fixture.statusShort),
        ),
      ),
    [sortedGameweeks],
  );

  const hasUpcomingFixtures = useMemo(() => {
    const now = Date.now();
    return sortedGameweeks.some((gw) => {
      const kickoffTs = new Date(gw.firstKickoff || 0).getTime();
      return Number.isFinite(kickoffTs) && kickoffTs > now;
    });
  }, [sortedGameweeks]);

  const selectedGameweek = useMemo(
    () => gameweeks.find((gw) => gw.id === selectedGameweekId) || null,
    [gameweeks, selectedGameweekId],
  );

  const scoreLeagues = leagues.filter(
    (league) => league.gameFormat === "score_prediction",
  );
  const losLeagues = leagues.filter(
    (league) => league.gameFormat === "last_one_standing",
  );
  const displayedLeagues = useMemo(() => {
    return [...scoreLeagues].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || "")),
    );
  }, [scoreLeagues]);
  const displayedLosLeagues = useMemo(() => {
    return [...losLeagues].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || "")),
    );
  }, [losLeagues]);
  const hasScoreLeagues = scoreLeagues.length > 0;

  const filteredScoreFixtures = useMemo(() => {
    const fixtures = selectedGameweek?.fixtures || [];
    return Array.isArray(fixtures) ? fixtures : [];
  }, [selectedGameweek]);
  // #endregion

  // #region Effects: Fetches leagues/gameweeks/scores/leaderboards and preloads LOS/standing data.
  useEffect(() => {
    const loadLeagues = async () => {
      setIsLoadingLeagues(true);
      setExpandedLeagueIds({});
      setActiveScoreLeagueId("");
      try {
        const res = await axiosInstance.get(`/api/v1/leagues`, {
          params: {
            username,
            competitionId: activeCompetitionId,
          },
        });
        setLeagues(res.data?.leagues || []);
        setLeagueError("");
        setLosStatusByLeague({});
        setLosErrorsByLeague({});
        setLosSavingByLeague({});
        setShowLosActiveUsersByLeague({});
        setScoreLeagueUserStandingByLeague({});
        setLosSelectedGameweekByLeague({});
        setLosPickByLeague({});
        setLosSelectionByLeague({});
      } catch (error) {
        setLeagues([]);
        setLeagueError("Could not load your leagues.");
      } finally {
        setIsLoadingLeagues(false);
      }
    };

    loadLeagues();
  }, [activeCompetitionId, username, leaguesRefreshSignal]);

  useEffect(() => {
    if (activeCompetitionId === null) return;
    setSelectedGameweekId("");
    setGameweeks([]);
    const fetchGameweeks = async () => {
      setIsLoadingGameweeks(true);
      try {
        const res = await axiosInstance.get(`/api/v1/gameweeks`, {
          params: { league: activeCompetitionId },
        });
        const gameweekData = Array.isArray(res.data)
          ? res.data
          : res.data?.gameweeks || [];
        setGameweeks(gameweekData);
        setScoreErrorMessage("");
      } catch (error) {
        setGameweeks([]);
        setScoreErrorMessage("Could not fetch matchdays.");
      } finally {
        setIsLoadingGameweeks(false);
      }
    };

    fetchGameweeks();
  }, [activeCompetitionId]);

  useEffect(() => {
    if (selectedGameweekId || sortedGameweeks.length === 0) return;

    // On page refresh (flag set) + first mount: restore the last-viewed gameweek.
    // On fresh login or competition change: always resolve current/live matchday.
    const isPageRefresh = sessionStorage.getItem("mm_session_init") === "true";
    if (!hasInitializedGameweek.current && isPageRefresh) {
      try {
        const saved = JSON.parse(
          localStorage.getItem("mm_gameweekSelection") || "null",
        );
        if (
          saved?.competitionId === activeCompetitionId &&
          saved?.gameweekId &&
          sortedGameweeks.some((gw) => gw.id === saved.gameweekId)
        ) {
          setSelectedGameweekId(saved.gameweekId);
          hasInitializedGameweek.current = true;
          return;
        }
      } catch {}
    }

    setSelectedGameweekId(getDefaultGameweekId(sortedGameweeks));
    hasInitializedGameweek.current = true;
  }, [sortedGameweeks, selectedGameweekId, activeCompetitionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeCompetitionId && selectedGameweekId) {
      localStorage.setItem(
        "mm_gameweekSelection",
        JSON.stringify({ competitionId: activeCompetitionId, gameweekId: selectedGameweekId }),
      );
    }
  }, [activeCompetitionId, selectedGameweekId]);

  useEffect(() => {
    const fetchSavedAndLeaderboard = async () => {
      if (!hasScoreLeagues || !selectedGameweekId) {
        setPredictions({});
        setFixturesWithScores({});
        setIsLocked(false);
        setLockTime(null);
        setLeaderboard([]);
        setSaveMessage("");
        return;
      }

      try {
        const savedRes = await axiosInstance.get(
          `/api/v1/predictions/${encodeURIComponent(selectedGameweekId)}`,
          {
            params: {
              username,
              league: activeCompetitionId,
            },
          },
        );

        const savedPredictions = {};
        (savedRes.data.predictions || []).forEach((p) => {
          savedPredictions[p.fixtureId] = {
            home: p.home,
            away: p.away,
          };
        });

        const fixtureScoreMap = {};
        (savedRes.data.fixtures || []).forEach((fixture) => {
          fixtureScoreMap[fixture.id] = {
            statusShort: fixture.statusShort,
            homeGoals: fixture.homeGoals,
            awayGoals: fixture.awayGoals,
            scoreDuration: fixture.scoreDuration || null,
          };
        });

        setPredictions(savedPredictions);
        setFixturesWithScores(fixtureScoreMap);
        setIsLocked(Boolean(savedRes.data.isLocked));
        setLockTime(savedRes.data.lockTime);
        setScoreErrorMessage("");
      } catch (error) {
        setScoreErrorMessage("Could not load saved predictions.");
      }
    };

    fetchSavedAndLeaderboard();
  // activeCompetitionId omitted: competition changes always reset selectedGameweekId first,
  // so firing on selectedGameweekId alone avoids a stale-gameweekId 404 when both state
  // updates land in the same React batch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasScoreLeagues, selectedGameweekId, username]);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      if (!hasScoreLeagues || !selectedGameweekId) {
        setLeaderboard([]);
        setCurrentMatchdayPointsByUser({});
        return;
      }

      try {
        const leaderboardRes =
          leaderboardMode === "season"
            ? await axiosInstance.get(`/api/v1/leaderboard/season`, {
                params: {
                  league: activeCompetitionId,
                  leagueId: activeScoreLeagueId || undefined,
                },
              })
            : await axiosInstance.get(`/api/v1/leaderboard`, {
                params: {
                  gameweekId: selectedGameweekId,
                  league: activeCompetitionId,
                  leagueId: activeScoreLeagueId || undefined,
                },
              });

        setLeaderboard(leaderboardRes.data.rankings || []);
        setIsSeasonComplete(
          leaderboardMode === "season"
            ? Boolean(leaderboardRes.data.isSeasonComplete)
            : false,
        );

        if (leaderboardMode === "season") {
          const currentMatchdayId = currentGameweekId || selectedGameweekId;
          if (currentMatchdayId) {
            const currentMatchdayRes = await axiosInstance.get(
              `/api/v1/leaderboard`,
              {
                params: {
                  gameweekId: currentMatchdayId,
                  league: activeCompetitionId,
                  leagueId: activeScoreLeagueId || undefined,
                },
              },
            );

            const matchdayPointsMap = {};
            (currentMatchdayRes.data?.rankings || []).forEach((entry) => {
              const key = entry.userId || entry.username;
              if (!key) return;
              matchdayPointsMap[key] = Number(entry.points || 0);
            });
            setCurrentMatchdayPointsByUser(matchdayPointsMap);
          } else {
            setCurrentMatchdayPointsByUser({});
          }
        } else {
          setCurrentMatchdayPointsByUser({});
        }
      } catch (error) {
        setLeaderboard([]);
        setCurrentMatchdayPointsByUser({});
        setScoreErrorMessage("Could not load leaderboard.");
      }
    };

    fetchLeaderboard();
  // activeCompetitionId omitted: same reason as predictions effect above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasScoreLeagues,
    selectedGameweekId,
    activeScoreLeagueId,
    leaderboardMode,
    currentGameweekId,
    username,
  ]);

  useEffect(() => {
    if (isLoadingLeagues) return;

    const scoreLeagueList = leagues.filter(
      (league) => league.gameFormat === "score_prediction",
    );

    if (scoreLeagueList.length === 0) {
      setScoreLeagueUserStandingByLeague({});
      return;
    }

    let cancelled = false;

    const loadScoreLeagueStandings = async () => {
      const responses = await Promise.allSettled(
        scoreLeagueList.map((league) =>
          axiosInstance.get(`/api/v1/leaderboard/season`, {
            params: {
              league: activeCompetitionId,
              leagueId: league.id,
            },
          }),
        ),
      );

      if (cancelled) return;

      const standings = {};
      responses.forEach((result, index) => {
        const leagueId = scoreLeagueList[index].id;
        if (result.status !== "fulfilled") {
          standings[leagueId] = null;
          return;
        }

        const rankings = result.value.data?.rankings || [];
        const userIndex = rankings.findIndex(
          (row) => (row.userId || row.username) === username,
        );

        if (userIndex < 0) {
          standings[leagueId] = null;
          return;
        }

        standings[leagueId] = {
          rank: rankings[userIndex].rank ?? userIndex + 1,
          points: rankings[userIndex].points,
        };
      });

      setScoreLeagueUserStandingByLeague(standings);
    };

    loadScoreLeagueStandings();

    return () => {
      cancelled = true;
    };
  }, [isLoadingLeagues, leagues, activeCompetitionId, username]);

  useEffect(() => {
    if (isLoadingLeagues || leagues.length === 0) return;

    const losLeagueList = leagues.filter(
      (league) => league.gameFormat === "last_one_standing",
    );
    const leaguesNeedingStatus = losLeagueList.filter(
      (league) => !losStatusByLeague[league.id],
    );

    if (leaguesNeedingStatus.length === 0) return;

    let cancelled = false;

    const preloadLosStatuses = async () => {
      const responses = await Promise.allSettled(
        leaguesNeedingStatus.map((league) =>
          axiosInstance.get(`/api/v1/los/status`, {
            params: { leagueId: league.id, username },
          }),
        ),
      );

      if (cancelled) return;

      const statusUpdates = {};
      responses.forEach((result, index) => {
        if (result.status !== "fulfilled") return;
        const leagueId = leaguesNeedingStatus[index].id;
        statusUpdates[leagueId] = result.value.data;
      });

      if (Object.keys(statusUpdates).length > 0) {
        setLosStatusByLeague((prev) => ({
          ...prev,
          ...statusUpdates,
        }));
      }
    };

    preloadLosStatuses();

    return () => {
      cancelled = true;
    };
  }, [isLoadingLeagues, leagues, losStatusByLeague, username]);
  // #endregion

  // #region Actions and Handlers: Update, submit, delete, toggle, and section render helper logic.
  const updateScore = (fixtureId, side, value, nativeEvent) => {
    setPredictions((prev) => {
      const previousValue =
        side === "home"
          ? (prev[fixtureId]?.home ?? "")
          : (prev[fixtureId]?.away ?? "");

      let normalized = value === "" ? "" : Math.max(0, Number(value));

      // Spinner/step-up from empty should start at 0, not 1.
      if (
        previousValue === "" &&
        String(value) === "1" &&
        nativeEvent?.data == null
      ) {
        normalized = 0;
      }

      return {
        ...prev,
        [fixtureId]: {
          home: side === "home" ? normalized : (prev[fixtureId]?.home ?? ""),
          away: side === "away" ? normalized : (prev[fixtureId]?.away ?? ""),
        },
      };
    });
  };

  const requiredFixtures = filteredScoreFixtures.filter(
    (fixture) =>
      !isFixtureBlockedForScore(
        fixturesWithScores[fixture.id]?.statusShort || fixture.statusShort,
      ),
  );

  const completePredictionsCount = requiredFixtures.filter((fixture) => {
    const p = predictions[fixture.id];
    return p && p.home !== "" && p.away !== "";
  }).length;

  const canSubmit =
    Boolean(selectedGameweek) &&
    requiredFixtures.length > 0 &&
    completePredictionsCount === requiredFixtures.length &&
    !isLocked;

  const handleScoreSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGameweek) {
      setScoreErrorMessage("Please select a matchday first.");
      return;
    }
    if (!canSubmit) {
      setScoreErrorMessage(
        "Please enter both home and away scores for every required fixture.",
      );
      return;
    }

    const payload = requiredFixtures.map((fixture) => ({
      fixtureId: fixture.id,
      home: Number(predictions[fixture.id].home),
      away: Number(predictions[fixture.id].away),
    }));

    setIsSubmitting(true);
    setScoreErrorMessage("");
    setSaveMessage("");

    try {
      await axiosInstance.post(`/api/v1/predictions`, {
        gameweekId: selectedGameweek.id,
        league: activeCompetitionId,
        username,
        predictions: payload,
      });

      setSaveMessage("Scores saved...");
      setShowSubmissionFlash(true);
      setTimeout(() => {
        setShowSubmissionFlash(false);
        setSaveMessage("");
      }, 1800);

      const leaderboardRes =
        leaderboardMode === "season"
          ? await axiosInstance.get(`/api/v1/leaderboard/season`, {
              params: {
                league: activeCompetitionId,
                leagueId: activeScoreLeagueId || undefined,
              },
            })
          : await axiosInstance.get(`/api/v1/leaderboard`, {
              params: {
                gameweekId: selectedGameweek.id,
                league: activeCompetitionId,
                leagueId: activeScoreLeagueId || undefined,
              },
            });

      setLeaderboard(leaderboardRes.data.rankings || []);
      setIsSeasonComplete(
        leaderboardMode === "season"
          ? Boolean(leaderboardRes.data.isSeasonComplete)
          : false,
      );

      if (leaderboardMode === "season") {
        const currentMatchdayId = currentGameweekId || selectedGameweek.id;
        if (currentMatchdayId) {
          const currentMatchdayRes = await axiosInstance.get(
            `/api/v1/leaderboard`,
            {
              params: {
                gameweekId: currentMatchdayId,
                league: activeCompetitionId,
                leagueId: activeScoreLeagueId || undefined,
              },
            },
          );

          const matchdayPointsMap = {};
          (currentMatchdayRes.data?.rankings || []).forEach((entry) => {
            const key = entry.userId || entry.username;
            if (!key) return;
            matchdayPointsMap[key] = Number(entry.points || 0);
          });
          setCurrentMatchdayPointsByUser(matchdayPointsMap);
        }
      } else {
        setCurrentMatchdayPointsByUser({});
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      setScoreErrorMessage(apiMessage || "Failed to submit predictions.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const hydrateLeagueCard = async (league) => {
    try {
      if (league.gameFormat === "last_one_standing") {
        const statusRes = await axiosInstance.get(`/api/v1/los/status`, {
          params: { leagueId: league.id, username },
        });

        setLosStatusByLeague((prev) => ({
          ...prev,
          [league.id]: statusRes.data,
        }));

        const defaultGameweekId =
          losSelectedGameweekByLeague[league.id] ||
          getDefaultGameweekId(sortedGameweeks);

        if (defaultGameweekId) {
          setLosSelectedGameweekByLeague((prev) => ({
            ...prev,
            [league.id]: defaultGameweekId,
          }));

          const pickRes = await axiosInstance.get(
            `/api/v1/los/pick/${encodeURIComponent(defaultGameweekId)}`,
            {
              params: { leagueId: league.id, username },
            },
          );

          setLosPickByLeague((prev) => ({
            ...prev,
            [league.id]: pickRes.data,
          }));

          if (pickRes.data?.pick) {
            setLosSelectionByLeague((prev) => ({
              ...prev,
              [league.id]: {
                fixtureId: pickRes.data.pick.fixtureId,
                pickedSide: pickRes.data.pick.pickedSide,
              },
            }));
          }
        }
      }

      setLosErrorsByLeague((prev) => ({ ...prev, [league.id]: "" }));
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      setLosErrorsByLeague((prev) => ({
        ...prev,
        [league.id]: apiMessage || "Could not load league details.",
      }));
    }
  };

  const toggleLeague = async (league) => {
    const isExpanded = Boolean(expandedLeagueIds[league.id]);

    setExpandedLeagueIds(() => (isExpanded ? {} : { [league.id]: true }));
    setActiveScoreLeagueId(
      !isExpanded && league.gameFormat === "score_prediction" ? league.id : "",
    );

    if (!isExpanded) {
      await hydrateLeagueCard(league);
    }
  };

  const handleLosGameweekChange = async (leagueId, gameweekId) => {
    setLosSelectedGameweekByLeague((prev) => ({
      ...prev,
      [leagueId]: gameweekId,
    }));

    try {
      const pickRes = await axiosInstance.get(
        `/api/v1/los/pick/${encodeURIComponent(gameweekId)}`,
        {
          params: { leagueId, username },
        },
      );

      setLosPickByLeague((prev) => ({
        ...prev,
        [leagueId]: pickRes.data,
      }));

      if (pickRes.data?.pick) {
        setLosSelectionByLeague((prev) => ({
          ...prev,
          [leagueId]: {
            fixtureId: pickRes.data.pick.fixtureId,
            pickedSide: pickRes.data.pick.pickedSide,
          },
        }));
      } else {
        setLosSelectionByLeague((prev) => ({
          ...prev,
          [leagueId]: { fixtureId: "", pickedSide: "" },
        }));
      }

      setLosErrorsByLeague((prev) => ({ ...prev, [leagueId]: "" }));
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      setLosErrorsByLeague((prev) => ({
        ...prev,
        [leagueId]: apiMessage || "Could not load LOS pick.",
      }));
    }
  };

  const handleLosSideChange = (leagueId, fixtureId, pickedSide) => {
    setLosSelectionByLeague((prev) => ({
      ...prev,
      [leagueId]: {
        fixtureId,
        pickedSide,
      },
    }));
  };

  const submitLosPick = async (league) => {
    const selection = losSelectionByLeague[league.id];
    const gameweekId = losSelectedGameweekByLeague[league.id];

    if (!gameweekId || !selection?.fixtureId || !selection?.pickedSide) {
      setLosErrorsByLeague((prev) => ({
        ...prev,
        [league.id]: "Pick a fixture and winner before submitting.",
      }));
      return;
    }

    setLosSavingByLeague((prev) => ({ ...prev, [league.id]: true }));
    try {
      const saveRes = await axiosInstance.post(`/api/v1/los/pick`, {
        leagueId: league.id,
        gameweekId,
        fixtureId: Number(selection.fixtureId),
        pickedSide: selection.pickedSide,
        username,
      });

      const savedPayload = saveRes.data || {};

      // Read back the persisted pick as source of truth after every save.
      const pickRes = await axiosInstance.get(
        `/api/v1/los/pick/${encodeURIComponent(gameweekId)}`,
        {
          params: { leagueId: league.id, username },
        },
      );

      const latestPick = pickRes.data?.pick || null;
      setLosPickByLeague((prev) => ({
        ...prev,
        [league.id]: {
          ...(prev[league.id] || {}),
          ...(pickRes.data || {}),
          gameweekId,
          isLocked:
            typeof pickRes.data?.isLocked === "boolean"
              ? pickRes.data.isLocked
              : Boolean(savedPayload.isLocked),
          lockTime:
            pickRes.data?.lockTime ||
            savedPayload.lockTime ||
            prev[league.id]?.lockTime ||
            null,
          firstKickoff:
            pickRes.data?.firstKickoff ||
            savedPayload.firstKickoff ||
            prev[league.id]?.firstKickoff ||
            null,
        },
      }));

      if (latestPick) {
        setLosSelectionByLeague((prev) => ({
          ...prev,
          [league.id]: {
            fixtureId: latestPick.fixtureId,
            pickedSide: latestPick.pickedSide,
          },
        }));
      }

      // Refresh LOS status so used-team disabling stays in sync after save.
      try {
        const statusRes = await axiosInstance.get(`/api/v1/los/status`, {
          params: { leagueId: league.id, username },
        });
        setLosStatusByLeague((prev) => ({
          ...prev,
          [league.id]: statusRes.data,
        }));
      } catch (statusError) {
        // Keep save successful even if status refresh fails transiently.
      }

      setLosErrorsByLeague((prev) => ({ ...prev, [league.id]: "" }));
      setLosFlashByLeague((prev) => ({ ...prev, [league.id]: true }));
      setTimeout(() => {
        setLosFlashByLeague((prev) => ({ ...prev, [league.id]: false }));
      }, 1800);
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      setLosErrorsByLeague((prev) => ({
        ...prev,
        [league.id]: apiMessage || "Could not save LOS pick.",
      }));
    } finally {
      setLosSavingByLeague((prev) => ({ ...prev, [league.id]: false }));
    }
  };

  const startLosNewRun = async (league) => {
    const confirmed = window.confirm(
      `Start a new run for ${league.name}? This resets all LOS picks for the current season.`,
    );
    if (!confirmed) return;

    setLosRestartingByLeague((prev) => ({ ...prev, [league.id]: true }));
    try {
      await axiosInstance.post(`/api/v1/los/run/restart`, {
        leagueId: league.id,
      });

      const statusRes = await axiosInstance.get(`/api/v1/los/status`, {
        params: { leagueId: league.id, username },
      });
      setLosStatusByLeague((prev) => ({
        ...prev,
        [league.id]: statusRes.data,
      }));

      const defaultGameweekId = getDefaultGameweekId(sortedGameweeks);
      if (defaultGameweekId) {
        setLosSelectedGameweekByLeague((prev) => ({
          ...prev,
          [league.id]: defaultGameweekId,
        }));

        const pickRes = await axiosInstance.get(
          `/api/v1/los/pick/${encodeURIComponent(defaultGameweekId)}`,
          {
            params: { leagueId: league.id, username },
          },
        );

        setLosPickByLeague((prev) => ({
          ...prev,
          [league.id]: pickRes.data,
        }));
      }

      setLosSelectionByLeague((prev) => ({
        ...prev,
        [league.id]: { fixtureId: "", pickedSide: "" },
      }));
      setLosErrorsByLeague((prev) => ({ ...prev, [league.id]: "" }));
      setLosActionMessageByLeague((prev) => ({
        ...prev,
        [league.id]: "New run started. Make your next pick.",
      }));
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      setLosErrorsByLeague((prev) => ({
        ...prev,
        [league.id]: apiMessage || "Could not start a new run.",
      }));
    } finally {
      setLosRestartingByLeague((prev) => ({ ...prev, [league.id]: false }));
    }
  };

  const deleteLeague = async (league, leagueLosStatus) => {
    const isLosLeague = league.gameFormat === "last_one_standing";
    const hasActiveLosPlayers =
      (leagueLosStatus?.activeUserIds || []).length > 1;

    let confirmMessage = `Delete league "${league.name}"? This cannot be undone.`;
    if (league.gameFormat === "score_prediction" && isScoreSeasonActive) {
      confirmMessage = `You are trying to delete an active Score Predictor league (season in progress).\n\nDelete "${league.name}" anyway?`;
    }
    if (isLosLeague && hasActiveLosPlayers) {
      confirmMessage = `You are trying to delete an active Last One Standing league (players still active).\n\nDelete "${league.name}" anyway?`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeletingLeagueById((prev) => ({ ...prev, [league.id]: true }));
    try {
      await axiosInstance.delete(`/api/v1/leagues/${league.id}`);

      setLeagues((prev) => prev.filter((item) => item.id !== league.id));
      setExpandedLeagueIds((prev) => {
        if (!prev[league.id]) return prev;
        return {};
      });
      if (activeScoreLeagueId === league.id) {
        setActiveScoreLeagueId("");
      }
      setLosStatusByLeague((prev) => {
        const { [league.id]: removed, ...rest } = prev;
        return rest;
      });
      setLosErrorsByLeague((prev) => {
        const { [league.id]: removed, ...rest } = prev;
        return rest;
      });
      setLosSavingByLeague((prev) => {
        const { [league.id]: removed, ...rest } = prev;
        return rest;
      });
      setLosSelectedGameweekByLeague((prev) => {
        const { [league.id]: removed, ...rest } = prev;
        return rest;
      });
      setLosPickByLeague((prev) => {
        const { [league.id]: removed, ...rest } = prev;
        return rest;
      });
      setLosSelectionByLeague((prev) => {
        const { [league.id]: removed, ...rest } = prev;
        return rest;
      });
      setLosFlashByLeague((prev) => {
        const { [league.id]: removed, ...rest } = prev;
        return rest;
      });
      setShowLosActiveUsersByLeague((prev) => {
        const { [league.id]: removed, ...rest } = prev;
        return rest;
      });
      setScoreLeagueUserStandingByLeague((prev) => {
        const { [league.id]: removed, ...rest } = prev;
        return rest;
      });
      setLeagueError("");
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      setLeagueError(apiMessage || "Could not delete league.");
    } finally {
      setDeletingLeagueById((prev) => ({ ...prev, [league.id]: false }));
    }
  };

  const leaveLeague = async (league) => {
    if (!window.confirm(`Leave "${league.name}"? You can rejoin with the league code.`)) return;
    setLeavingLeagueById((prev) => ({ ...prev, [league.id]: true }));
    try {
      await axiosInstance.delete(`/api/v1/leagues/${encodeURIComponent(league.id)}/leave`);
      setLeagues((prev) => prev.filter((l) => l.id !== league.id));
      setExpandedLeagueIds((prev) => {
        if (!prev[league.id]) return prev;
        return {};
      });
      if (activeScoreLeagueId === league.id) setActiveScoreLeagueId("");
      setLosStatusByLeague((prev) => { const { [league.id]: _, ...rest } = prev; return rest; });
      setScoreLeagueUserStandingByLeague((prev) => { const { [league.id]: _, ...rest } = prev; return rest; });
      setLeagueError("");
    } catch (err) {
      setLeagueError(err?.response?.data?.message || "Could not leave league.");
    } finally {
      setLeavingLeagueById((prev) => { const { [league.id]: _, ...rest } = prev; return rest; });
    }
  };

  const getPredictionOutcome = (fixtureId) => {
    const predicted = predictions[fixtureId];
    const actual = fixturesWithScores[fixtureId];
    if (!predicted || !actual) return "pending";
    if (actual.homeGoals === null || actual.awayGoals === null)
      return "pending";

    const predictedHome = Number(predicted.home);
    const predictedAway = Number(predicted.away);
    const actualHome = Number(actual.homeGoals);
    const actualAway = Number(actual.awayGoals);

    if (predictedHome === actualHome && predictedAway === actualAway) {
      return "exact";
    }

    const predictedResult =
      predictedHome > predictedAway
        ? "HOME"
        : predictedAway > predictedHome
          ? "AWAY"
          : "DRAW";
    const actualResult =
      actualHome > actualAway
        ? "HOME"
        : actualAway > actualHome
          ? "AWAY"
          : "DRAW";

    if (predictedResult === actualResult) return "result";
    return "incorrect";
  };

  const matchdayPoints = filteredScoreFixtures.reduce((total, fixture) => {
    const outcome = getPredictionOutcome(fixture.id);
    if (outcome === "exact") return total + 3;
    if (outcome === "result") return total + 1;
    return total;
  }, 0);

  const renderSharedFixturesPanel = () => {
    return (
      <section className="shared-score-panel shared-fixtures-panel predictions-section">
        {isLoadingGameweeks && <p>Loading matchdays...</p>}
        {scoreErrorMessage && (
          <p className="status-message">{scoreErrorMessage}</p>
        )}

        {!isLoadingGameweeks && sortedGameweeks.length === 0 && (
          <div className="season-complete-banner">
            <span className="season-complete-icon">&#9201;</span>
            <span>New season coming soon — check back when fixtures are available.</span>
          </div>
        )}

        {!isLoadingGameweeks && sortedGameweeks.length > 0 && (
          <>
            <div className="fixtures-top-row">
              <div className="fixtures-select-row">
                <select
                  id="gameweek-shared"
                  className="gameweek-select"
                  value={selectedGameweekId}
                  onChange={(e) => {
                    setSelectedGameweekId(e.target.value);
                    setScoreErrorMessage("");
                  }}
                >
                  <option value="">Choose...</option>
                  {sortedGameweeks.map((gw) => (
                    <option key={gw.id} value={gw.id}>
                      {gw.id}
                      {gw.id === currentGameweekId && hasUpcomingFixtures ? " (CURRENT)" : ""}
                    </option>
                  ))}
                </select>
                <span
                  className="info-toggle-btn"
                  role="button"
                  tabIndex={0}
                  aria-expanded={showScoreSectionRulesInfo}
                  aria-controls="score-section-rules"
                  aria-label={
                    showScoreSectionRulesInfo
                      ? "Hide Score Predictor rules"
                      : "Show Score Predictor rules"
                  }
                  title={
                    showScoreSectionRulesInfo
                      ? "Hide Score Predictor rules"
                      : "Show Score Predictor rules"
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowScoreSectionRulesInfo((prev) => !prev);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowScoreSectionRulesInfo((prev) => !prev);
                    }
                  }}
                >
                  i
                </span>
              </div>

            </div>
            {showScoreSectionRulesInfo && (
              <div id="score-section-rules" className="game-info-card">
                <p className="helper-text">
                  This section uses the shared score predictions for the
                  selected competition.
                </p>
                <ul className="game-rules-list">
                  <li>
                    Submit one home and away score for each fixture.
                  </li>
                  <li>
                    Predictions lock 30 minutes before the first kickoff of the
                    selected matchday.
                  </li>
                  <li>
                    Exact score = 3 points, correct result (win/draw/loss) = 1
                    point.
                  </li>
                  <li>
                    Scores are based on the full open-play result — full time
                    including injury time, plus any extra time goals. Penalty
                    shootouts are excluded entirely.
                  </li>
                  <li>
                    If you think a game will go to penalties, predict the score you expect
                    at the end of extra time — e.g. predicting 1-1 scores
                    maximum points if it stays 1-1 through ET regardless of
                    who wins the shootout.
                  </li>
                  <li>
                    Postponed, suspended, or cancelled fixtures are excluded
                    from scoring.
                  </li>
                  <li>
                    Leaderboards are available in Matchday and Season Total
                    views.
                  </li>
                </ul>
              </div>
            )}

            {!hasUpcomingFixtures && (
              <div className="season-complete-banner season-complete-banner--sm">
                <span className="season-complete-icon">&#127937;</span>
                <span>
                  {isSeasonComplete
                    ? "Season complete — new season coming soon"
                    : "No upcoming fixtures — new season coming soon"}
                </span>
              </div>
            )}
          </>
        )}

        {isLocked ? (
          <p className="status-message lock-message">Predictions locked</p>
        ) : lockTime ? (
          <p className="status-message deadline-message">
            Deadline:{" "}
            {new Date(lockTime).toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            })}
            ,{" "}
            {new Date(lockTime).toLocaleDateString(undefined, {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })}
          </p>
        ) : null}

        {selectedGameweek && filteredScoreFixtures.length > 0 && (
          <form onSubmit={handleScoreSubmit} className="fixtures-form">
            <div className="fixtures-progress-row">
              <p className="helper-text fixtures-matchday-points">
                {matchdayPoints} pts
              </p>
              <p className="helper-text fixtures-completed">
                Completed: {completePredictionsCount}/{requiredFixtures.length}
              </p>
              <ChevronToggleButton
                isExpanded={areScoreFixturesExpanded}
                onClick={() => setAreScoreFixturesExpanded((prev) => !prev)}
                controlsId="shared-score-fixtures-list"
                expandedLabel="Collapse fixtures"
                collapsedLabel="Expand fixtures"
                expandedTitle="Collapse fixtures"
                collapsedTitle="Expand fixtures"
              />
            </div>

            {areScoreFixturesExpanded && (
              <div id="shared-score-fixtures-list">
                {(() => {
                  const groups = [];
                  filteredScoreFixtures.forEach((fixture) => {
                    const dateKey = fixture.date
                      ? new Date(fixture.date).toLocaleDateString(undefined, {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                        })
                      : "Date TBC";
                    const last = groups[groups.length - 1];
                    if (!last || last.dateKey !== dateKey) {
                      groups.push({ dateKey, fixtures: [fixture] });
                    } else {
                      last.fixtures.push(fixture);
                    }
                  });

                  return groups.flatMap((group) => [
                    <div
                      key={`date-${group.dateKey}`}
                      className="fixture-date-divider"
                    >
                      <span>{group.dateKey}</span>
                    </div>,
                    ...group.fixtures.map((fixture) => {
                      const status =
                        fixturesWithScores[fixture.id]?.statusShort ||
                        fixture.statusShort;
                      const predicted = predictions[fixture.id];

                      return (
                        <div key={fixture.id} className="fixture-row">
                          <div className="fixture-title">
                            {renderTeamLabel(fixture.home, fixture.homeCrest)}
                            <span className="vs">vs</span>
                            {renderTeamLabel(fixture.away, fixture.awayCrest)}
                            <span
                              className={`fixture-status-chip ${getFixtureStatusClass(status)}`}
                            >
                              {getFixtureStatusLabel(status)}
                            </span>
                            {fixture.date && !isFixtureFinished(status) && (
                              <span className="fixture-kickoff-time">
                                {new Date(fixture.date).toLocaleTimeString(
                                  undefined,
                                  { hour: "2-digit", minute: "2-digit" },
                                )}
                              </span>
                            )}
                          </div>

                          <div className="prediction-block">
                            <div
                              className={`score-inputs prediction-${getPredictionOutcome(fixture.id)} ${showSubmissionFlash ? "submission-flash" : ""}`}
                            >
                              <input
                                className="score-input"
                                type="number"
                                min="0"
                                value={predicted?.home ?? ""}
                                onChange={(e) =>
                                  updateScore(
                                    fixture.id,
                                    "home",
                                    e.target.value,
                                    e.nativeEvent,
                                  )
                                }
                                disabled={
                                  isLocked || isFixtureBlockedForScore(status)
                                }
                                placeholder="H"
                              />
                              <input
                                className="score-input"
                                type="number"
                                min="0"
                                value={predicted?.away ?? ""}
                                onChange={(e) =>
                                  updateScore(
                                    fixture.id,
                                    "away",
                                    e.target.value,
                                    e.nativeEvent,
                                  )
                                }
                                disabled={
                                  isLocked || isFixtureBlockedForScore(status)
                                }
                                placeholder="A"
                              />
                            </div>

                            {((predicted &&
                              predicted.home !== "" &&
                              predicted.away !== "") ||
                              (isFixtureFinished(status) &&
                                fixturesWithScores[fixture.id]?.homeGoals !=
                                  null)) && (
                                <div className="prediction-summary">
                                  <span>
                                    Actual:{" "}
                                    {fixturesWithScores[fixture.id]
                                      ?.homeGoals ?? "-"}{" "}
                                    -{" "}
                                    {fixturesWithScores[fixture.id]
                                      ?.awayGoals ?? "-"}
                                    {fixturesWithScores[fixture.id]
                                      ?.scoreDuration === "PENALTY_SHOOTOUT" && (
                                      <span className="score-duration-chip">PEN</span>
                                    )}
                                    {fixturesWithScores[fixture.id]
                                      ?.scoreDuration === "EXTRA_TIME" && (
                                      <span className="score-duration-chip">AET</span>
                                    )}
                                  </span>
                                </div>
                              )}
                          </div>
                        </div>
                      );
                    }),
                  ]);
                })()}

                <div className="submit-row">
                  <button
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                    className="submit-btn"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Predictions"}
                  </button>
                  {saveMessage && (
                    <span className="save-message">{saveMessage}</span>
                  )}
                </div>
              </div>
            )}
          </form>
        )}
      </section>
    );
  };

  const renderLeaderboardPanel = (league) => {
    const ROWS_PER_PAGE = 10;
    const isExpanded = expandedLeaderboards[league.id] || false;
    const userLeaderboardIndex = leaderboard.findIndex(
      (row) => (row.userId || row.username) === username,
    );

    let displayedLeaderboardRows;
    let userPage = 0;
    let totalPages = 1;

    if (isExpanded) {
      const currentPage = leaderboardPages[league.id] || 0;
      totalPages = Math.ceil(leaderboard.length / ROWS_PER_PAGE);
      userPage = Math.floor(userLeaderboardIndex / ROWS_PER_PAGE);
      const startIdx = currentPage * ROWS_PER_PAGE;
      const endIdx = startIdx + ROWS_PER_PAGE;
      displayedLeaderboardRows = leaderboard.slice(startIdx, endIdx);
    } else {
      const showLeaderboardGap = userLeaderboardIndex >= 5;
      displayedLeaderboardRows = showLeaderboardGap
        ? [...leaderboard.slice(0, 5), leaderboard[userLeaderboardIndex]]
        : leaderboard.slice(0, 5);
    }

    return (
      <section className="shared-score-panel">
        {selectedGameweekId ? (
          <section className="leaderboard-card">
            <div className="leaderboard-header">
              <h3>Competition Leaderboard</h3>
              <div className="leaderboard-controls">
                <select
                  id="leaderboardMode"
                  className="mode-select"
                  value={
                    leaderboardMode === "season"
                      ? "season"
                      : selectedGameweekId
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "season") {
                      setLeaderboardMode("season");
                      return;
                    }
                    setLeaderboardMode("matchweek");
                    setSelectedGameweekId(value);
                    setScoreErrorMessage("");
                  }}
                >
                  <option value="season">Season Total</option>
                  {sortedGameweeks.map((gw) => (
                    <option key={gw.id} value={gw.id}>
                      {gw.id}
                      {gw.id === currentGameweekId && hasUpcomingFixtures
                        ? " (CURRENT)"
                        : ""}
                    </option>
                  ))}
                </select>
                <ChevronToggleButton
                  isExpanded={isExpanded}
                  onClick={() =>
                    setExpandedLeaderboards((prev) => ({
                      ...prev,
                      [league.id]: !prev[league.id],
                    }))
                  }
                  controlsId={`leaderboard-${league.id}`}
                  expandedLabel="Collapse standings"
                  collapsedLabel="Expand standings"
                  expandedTitle="Collapse full standings"
                  collapsedTitle="Expand full standings"
                />
              </div>
            </div>

            {isSeasonComplete && leaderboardMode === "season" && leaderboard.length > 0 && (() => {
              const currentUserIsWinner = leaderboard.some(
                (row) => row.rank === 1 && (row.userId || row.username) === username,
              );
              const winners = leaderboard.filter((row) => row.rank === 1);
              return currentUserIsWinner ? (
                <div className="season-winner-banner">
                  <span className="season-winner-trophy">&#127942;</span>
                  <div>
                    <p className="season-winner-heading">You won the league!</p>
                    <p className="season-winner-sub">
                      {leaderboard.find((r) => (r.userId || r.username) === username)?.points} pts &middot; {leaderboard.find((r) => (r.userId || r.username) === username)?.exactScores} exact scores &mdash; well played.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="season-complete-banner">
                  <span className="season-complete-icon">&#127937;</span>
                  <span>
                    Season complete &mdash;{" "}
                    {winners.length === 1
                      ? `${winners[0].username} wins!`
                      : `${winners.map((w) => w.username).join(" & ")} tied for the win!`}
                  </span>
                </div>
              );
            })()}

            {leaderboard.length === 0 ? (
              <p>No leaderboard data yet.</p>
            ) : (
              <div className="leaderboard-table-wrap">
                <table className="leaderboard-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Player</th>
                      <th>Pts</th>
                      <th>Exact</th>
                      <th>
                        {leaderboardMode === "season"
                          ? "MD Pts"
                          : "Correct Results"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedLeaderboardRows.map((row, index) => {
                      const rowKey = row.userId || row.username;
                      const absoluteRank = row.rank ?? index + 1;
                      const isCurrentUser = rowKey === username;
                      const isLeagueWinner =
                        isSeasonComplete &&
                        leaderboardMode === "season" &&
                        absoluteRank === 1;

                      return (
                        <React.Fragment key={rowKey}>
                          {!isExpanded &&
                            userLeaderboardIndex >= 5 &&
                            index === 5 && (
                              <tr className="leaderboard-row-gap">
                                <td colSpan={5}>...</td>
                              </tr>
                            )}
                          <tr
                            className={
                              isCurrentUser
                                ? "leaderboard-row-current"
                                : isLeagueWinner
                                  ? "leaderboard-row-winner"
                                  : undefined
                            }
                          >
                            <td>{absoluteRank}</td>
                            <td>
                              {isLeagueWinner && (
                                <span className="leaderboard-winner-cup">&#127942;</span>
                              )}
                              {row.username}
                            </td>
                            <td>{row.points}</td>
                            <td>{row.exactScores}</td>
                            <td>
                              {leaderboardMode === "season"
                                ? isCurrentUser && selectedGameweekId === currentGameweekId
                                  ? matchdayPoints
                                  : (currentMatchdayPointsByUser[rowKey] ?? 0)
                                : row.correctResults}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                {isExpanded && totalPages > 1 && (
                  <div className="leaderboard-pagination">
                    <button
                      disabled={leaderboardPages[league.id] === 0}
                      onClick={() =>
                        setLeaderboardPages((prev) => ({
                          ...prev,
                          [league.id]: Math.max(0, (prev[league.id] || 0) - 1),
                        }))
                      }
                      title="Previous page"
                    >
                      ←
                    </button>
                    <span className="pagination-info">
                      {(leaderboardPages[league.id] || 0) + 1}/{totalPages}
                      {userPage > leaderboardPages[league.id] && (
                        <span className="user-on-page"> (you: {userPage + 1})</span>
                      )}
                    </span>
                    <button
                      disabled={leaderboardPages[league.id] >= totalPages - 1}
                      onClick={() =>
                        setLeaderboardPages((prev) => ({
                          ...prev,
                          [league.id]: Math.min(
                            totalPages - 1,
                            (prev[league.id] || 0) + 1,
                          ),
                        }))
                      }
                      title="Next page"
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
        ) : (
          <p className="helper-text">Choose a matchday to view leaderboard.</p>
        )}
      </section>
    );
  };

  // #region Render: Competition tabs and league-card UI for score predictor and LOS modes.
  return (
    <section className="predictions-card">
      <div className="card-heading-row">
        <h2>Games</h2>
        <select
          className="gameweek-select competition-select"
          value={activeCompetitionId ?? ""}
          onChange={(e) => onCompetitionChange?.(Number(e.target.value))}
          aria-label="Select competition"
          disabled={activeCompetitionId === null}
        >
          {COMPETITIONS.map((competition) => (
            <option key={competition.id} value={competition.id}>
              {competition.label}
            </option>
          ))}
        </select>
      </div>

      {leagueError && <p className="status-message">{leagueError}</p>}

      {isLoadingLeagues && <p>Loading leagues...</p>}

      {!isLoadingLeagues && leagues.length === 0 && (
        <p className="helper-text">
          You are not in any leagues for this competition yet.
        </p>
      )}

      {hasScoreLeagues && (
        <>
          {renderSharedFixturesPanel()}

          <div className="section-break">
            <h3>Score Predictor Leagues</h3>
          </div>
        </>
      )}

      <section className="league-list">
        {displayedLeagues.map((league) => {
          const isExpanded = Boolean(expandedLeagueIds[league.id]);
          const isOwnedByUser = league.ownerUsername === username;
          const isDeletingLeague = Boolean(deletingLeagueById[league.id]);
          const isLeavingLeague = Boolean(leavingLeagueById[league.id]);
          const isGlobalScoreLeague =
            league.gameFormat === "score_prediction" &&
            league.ownerUsername === "GLOBAL_LEAGUE_SYSTEM";
          const userScoreLeagueStanding =
            scoreLeagueUserStandingByLeague[league.id];
          const leagueLosStatus = losStatusByLeague[league.id];
          const leagueLosPick = losPickByLeague[league.id];
          const activeLosUserIds =
            leagueLosStatus?.activeUserIds ||
            leagueLosStatus?.activeUsers ||
            [];
          const activeLosUsers = leagueLosStatus?.activeUsers || [];
          const eliminatedLosUsers = leagueLosStatus?.eliminatedUsers || [];
          const usedTeamsForUser =
            leagueLosStatus?.usedTeamsByUser?.[username] || [];
          const isUserActiveInLosRun = Boolean(
            activeLosUserIds.includes(username),
          );
          const isLosFinished = Boolean(leagueLosStatus?.isFinished);
          const losFinishedReason = leagueLosStatus?.finishedReason || "";
          const losFinishedGameweekId = leagueLosStatus?.finishedGameweekId;
          const losRunLength = Number(leagueLosStatus?.runLength || 0);
          const losHasUpcomingGameweeks = Boolean(leagueLosStatus?.hasUpcomingGameweeks);
          const displayActiveLosUsers = isLosFinished ? [] : activeLosUsers;
          const isLosActiveUsersExpanded = Boolean(
            showLosActiveUsersByLeague[league.id],
          );
          const inPlayEntry = leagueLosStatus?.history?.find((e) => e.inPlay);
          const pickedByTeam = {};
          const noPickYet = [];
          if (inPlayEntry) {
            for (const result of inPlayEntry.results) {
              if (result.pickedTeam) {
                (pickedByTeam[result.pickedTeam] ??= []).push(result.username);
              } else {
                noPickYet.push(result.username);
              }
            }
          }
          const isLosEliminatedExpanded = Boolean(
            showLosEliminatedByLeague[league.id],
          );
          const isRestartingLosRun = Boolean(losRestartingByLeague[league.id]);
          const selectedLosGameweekId =
            losSelectedGameweekByLeague[league.id] ||
            getDefaultGameweekId(sortedGameweeks);
          const selectedLosGameweek = sortedGameweeks.find(
            (gw) => gw.id === selectedLosGameweekId,
          );
          const losSelection = losSelectionByLeague[league.id] || {
            fixtureId: "",
            pickedSide: "",
          };

          return (
            <article
              key={league.id}
              className={`league-card ${
                isOwnedByUser ? "league-card-owner" : "league-card-guest"
              }`}
            >
              <button
                type="button"
                className="league-header"
                onClick={() => toggleLeague(league)}
              >
                <div className="league-header-main">
                  <div className="league-header-top">
                    <h3>{league.name}</h3>
                    <div className="league-meta">
                      <span
                        className={`league-badge ${
                          isOwnedByUser
                            ? "league-role-owner"
                            : "league-role-guest"
                        }`}
                      >
                        {isOwnedByUser
                          ? "Your league"
                          : isGlobalScoreLeague
                            ? "Global League"
                            : `${league.ownerDisplayName || league.ownerUsername || "Owner"}'s league`}
                      </span>
                      <span className="league-badge">{league.visibility}</span>
                      {league.gameFormat === "last_one_standing" && (
                        <div className="league-inline-status">
                          <span
                            className={`league-badge ${
                              !leagueLosStatus
                                ? "los-run-badge-loading"
                                : isLosFinished && losFinishedReason === "winner" && isUserActiveInLosRun
                                  ? "los-run-badge-winner"
                                  : isUserActiveInLosRun
                                    ? "los-run-badge-active"
                                    : "los-run-badge-eliminated"
                            }`}
                          >
                            {!leagueLosStatus
                              ? "Loading..."
                              : isLosFinished && losFinishedReason === "winner" && isUserActiveInLosRun
                                ? "Winner"
                                : isLosFinished && losFinishedReason === "eliminated"
                                  ? "Game Over"
                                  : isUserActiveInLosRun
                                    ? "Active"
                                    : "Eliminated"}
                          </span>
                        </div>
                      )}
                      {isOwnedByUser && (
                        <span
                          className={`league-delete-icon ${isDeletingLeague ? "busy" : ""}`}
                          role="button"
                          tabIndex={0}
                          title="Delete league"
                          aria-label={`Delete league ${league.name}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isDeletingLeague) {
                              deleteLeague(league, leagueLosStatus);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!isDeletingLeague) {
                                deleteLeague(league, leagueLosStatus);
                              }
                            }
                          }}
                        >
                          {isDeletingLeague ? "..." : "🗑"}
                        </span>
                      )}
                      {!isOwnedByUser && !isGlobalScoreLeague && (
                        <span
                          className={`league-leave-btn ${isLeavingLeague ? "busy" : ""}`}
                          role="button"
                          tabIndex={0}
                          title="Leave league"
                          aria-label={`Leave league ${league.name}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isLeavingLeague) leaveLeague(league);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!isLeavingLeague) leaveLeague(league);
                            }
                          }}
                        >
                          {isLeavingLeague ? "..." : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                              <polyline points="16 17 21 12 16 7"/>
                              <line x1="21" y1="12" x2="9" y2="12"/>
                            </svg>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="league-header-bottom">
                    {league.gameFormat === "score_prediction" &&
                      userScoreLeagueStanding && (
                        <p className="league-user-standing">
                          {`#${userScoreLeagueStanding.rank}/${league.memberCount || 0} | ${userScoreLeagueStanding.points} pts`}
                        </p>
                      )}
                  </div>
                </div>
              </button>

              {isExpanded && (
                <div
                  className={`league-content ${
                    league.gameFormat === "score_prediction"
                      ? "league-content--score"
                      : ""
                  }`}
                >
                  {losErrorsByLeague[league.id] && (
                    <p className="status-message">
                      {losErrorsByLeague[league.id]}
                    </p>
                  )}

                  {league.gameFormat === "score_prediction" &&
                    renderLeaderboardPanel(league)}

                  {league.gameFormat === "last_one_standing" && (
                    <div className="los-panel">
                      {leagueLosStatus && (
                        <div className="los-active-users-block">
                          <div className="los-active-users-header">
                            <p className="helper-text">
                              Run {leagueLosStatus.currentRunNumber} | Active
                              users: {displayActiveLosUsers.length}
                              {leagueLosStatus.tie
                                ? " | Current season result: tie"
                                : ""}
                              {isLosFinished && losFinishedGameweekId
                                ? ` | Ended: ${losFinishedGameweekId}`
                                : ""}
                            </p>
                            <ChevronToggleButton
                              isExpanded={isLosActiveUsersExpanded}
                              onClick={() =>
                                setShowLosActiveUsersByLeague((prev) => ({
                                  ...prev,
                                  [league.id]: !prev[league.id],
                                }))
                              }
                              controlsId={`los-active-users-${league.id}`}
                              expandedLabel="Hide active users list"
                              collapsedLabel="Show active users list"
                              expandedTitle="Hide active users"
                              collapsedTitle="Show active users"
                            />
                          </div>

                          {isLosActiveUsersExpanded && (
                            <div id={`los-active-users-${league.id}`}>
                              {displayActiveLosUsers.length === 0 ? (
                                <p className="helper-text los-active-users-list">
                                  No active users left.
                                </p>
                              ) : inPlayEntry ? (
                                <div className="los-picks-summary">
                                  {Object.keys(pickedByTeam).length > 0 && (
                                    <>
                                      <p className="helper-text los-section-label">Picked:</p>
                                      {Object.entries(pickedByTeam).map(([team, users]) => (
                                        <p key={team} className="helper-text los-pick-row">
                                          {team} — {users.join(", ")}
                                        </p>
                                      ))}
                                    </>
                                  )}
                                  {noPickYet.length > 0 && (
                                    <>
                                      <p className="helper-text los-section-label">No pick:</p>
                                      <p className="helper-text los-pick-row">{noPickYet.join(", ")}</p>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <p className="helper-text los-active-users-list">
                                  {displayActiveLosUsers.join(", ")}
                                </p>
                              )}
                              {eliminatedLosUsers.length > 0 && (
                                <div className="los-eliminated-users">
                                  <div className="los-eliminated-header">
                                    <p className="helper-text los-section-label">
                                      Eliminated:
                                    </p>
                                    <ChevronToggleButton
                                      isExpanded={isLosEliminatedExpanded}
                                      onClick={() =>
                                        setShowLosEliminatedByLeague((prev) => ({
                                          ...prev,
                                          [league.id]: !prev[league.id],
                                        }))
                                      }
                                      controlsId={`los-eliminated-${league.id}`}
                                      expandedLabel="Hide eliminated list"
                                      collapsedLabel="Show eliminated list"
                                      expandedTitle="Hide eliminated"
                                      collapsedTitle="Show eliminated"
                                    />
                                  </div>
                                  {isLosEliminatedExpanded && (
                                    <p
                                      id={`los-eliminated-${league.id}`}
                                      className="helper-text los-pick-row"
                                    >
                                      {eliminatedLosUsers.map((u) => u.username).join(", ")}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {isLosFinished && losFinishedReason === "winner" && (() => {
                        const losWinner = activeLosUsers[0];
                        return losWinner === username ? (
                          <div className="season-winner-banner">
                            <span className="season-winner-trophy">&#127942;</span>
                            <div>
                              <p className="season-winner-heading">You won the run!</p>
                              <p className="season-winner-sub">
                                Last one standing &mdash; well played.
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="season-complete-banner">
                            <span className="season-complete-icon">&#127942;</span>
                            <span>Winner: {losWinner}</span>
                          </div>
                        );
                      })()}

                      {isLosFinished && losFinishedReason === "eliminated" && (
                        <p className="status-message lock-message">
                          Eliminated on{" "}
                          {losFinishedGameweekId || "a previous matchday"}. Run
                          length: {losRunLength} matchday
                          {losRunLength === 1 ? "" : "s"}. This LOS run is
                          finished.
                        </p>
                      )}

                      {isOwnedByUser && isLosFinished && losHasUpcomingGameweeks && (
                        <div className="submit-row">
                          <button
                            type="button"
                            className="submit-btn"
                            onClick={() => startLosNewRun(league)}
                            disabled={isRestartingLosRun}
                          >
                            {isRestartingLosRun
                              ? "Starting..."
                              : "Start New Run"}
                          </button>
                        </div>
                      )}

                      {losActionMessageByLeague[league.id] && (
                        <p className="status-message">
                          {losActionMessageByLeague[league.id]}
                        </p>
                      )}

                      <label
                        className="field-label"
                        htmlFor={`los-gw-${league.id}`}
                      >
                        Matchday
                      </label>
                      <select
                        id={`los-gw-${league.id}`}
                        className="gameweek-select"
                        value={selectedLosGameweekId || ""}
                        onChange={(e) =>
                          handleLosGameweekChange(league.id, e.target.value)
                        }
                      >
                        <option value="">Choose...</option>
                        {sortedGameweeks.map((gw) => (
                          <option key={gw.id} value={gw.id}>
                            {gw.id}
                            {gw.id === currentGameweekId && hasUpcomingFixtures ? " (CURRENT)" : ""}
                          </option>
                        ))}
                      </select>

                      {leagueLosPick?.isLocked ? (
                        <p className="status-message lock-message">Picks locked</p>
                      ) : leagueLosPick?.lockTime ? (
                        <p className="status-message deadline-message">
                          Deadline:{" "}
                          {new Date(leagueLosPick.lockTime).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                          ,{" "}
                          {new Date(leagueLosPick.lockTime).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" })}
                        </p>
                      ) : null}

                      {selectedLosGameweek && (
                        <div className="los-fixture-list">
                          {selectedLosGameweek.fixtures.map((fixture) => {
                            const status = fixture.statusShort || "SCHEDULED";
                            const selectedFixture =
                              Number(losSelection.fixtureId) ===
                              Number(fixture.id);
                            const isHomeUsed = isTeamAlreadyUsed(
                              fixture.home,
                              usedTeamsForUser,
                            );
                            const isAwayUsed = isTeamAlreadyUsed(
                              fixture.away,
                              usedTeamsForUser,
                            );
                            const isLocked = Boolean(leagueLosPick?.isLocked);
                            const isSelectionDisabled =
                              isLocked ||
                              !isUserActiveInLosRun ||
                              isLosFinished;
                            return (
                              <div key={fixture.id} className="los-fixture-row">
                                <div className="los-pick-options">
                                  <button
                                    type="button"
                                    className={`los-side-btn ${selectedFixture && losSelection.pickedSide === "HOME" ? "active" : ""} ${isHomeUsed ? "used" : ""}`}
                                    onClick={() =>
                                      handleLosSideChange(
                                        league.id,
                                        fixture.id,
                                        "HOME",
                                      )
                                    }
                                    disabled={isSelectionDisabled || isHomeUsed}
                                  >
                                    {renderTeamLabel(
                                      fixture.home,
                                      fixture.homeCrest,
                                    )}
                                    {isHomeUsed && (
                                      <span className="los-used-tag">Used</span>
                                    )}
                                  </button>
                                  <span className="los-pick-divider">vs</span>
                                  <button
                                    type="button"
                                    className={`los-side-btn ${selectedFixture && losSelection.pickedSide === "AWAY" ? "active" : ""} ${isAwayUsed ? "used" : ""}`}
                                    onClick={() =>
                                      handleLosSideChange(
                                        league.id,
                                        fixture.id,
                                        "AWAY",
                                      )
                                    }
                                    disabled={isSelectionDisabled || isAwayUsed}
                                  >
                                    {renderTeamLabel(
                                      fixture.away,
                                      fixture.awayCrest,
                                    )}
                                    {isAwayUsed && (
                                      <span className="los-used-tag">Used</span>
                                    )}
                                  </button>
                                  <span
                                    className={`fixture-status-chip ${getFixtureStatusClass(status)}`}
                                  >
                                    {getFixtureStatusLabel(status)}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {leagueLosPick?.pick && (
                        <p
                          className={`los-saved-pick${
                            losFlashByLeague[league.id] ? " los-pick-flash" : ""
                          }`}
                        >
                          Saved pick:{" "}
                          <strong>{leagueLosPick.pick.pickedTeam}</strong> (
                          {leagueLosPick.pick.pickedSide})
                          {losFlashByLeague[league.id] && (
                            <span className="los-pick-updated"> ✓ Updated</span>
                          )}
                        </p>
                      )}

                      <div className="submit-row">
                        <button
                          type="button"
                          className="submit-btn"
                          disabled={
                            Boolean(leagueLosPick?.isLocked) ||
                            isLosFinished ||
                            !isUserActiveInLosRun ||
                            Boolean(losSavingByLeague[league.id])
                          }
                          onClick={() => submitLosPick(league)}
                        >
                          {losSavingByLeague[league.id]
                            ? "Saving..."
                            : leagueLosPick?.pick
                              ? "Update Pick"
                              : "Submit LOS Pick"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </section>

      {displayedLosLeagues.length > 0 && (
        <>
          <div className="section-break">
            <h3>Last One Standing</h3>
            <span
              className="info-toggle-btn"
              role="button"
              tabIndex={0}
              aria-expanded={showLosSectionRulesInfo}
              aria-controls="los-section-rules"
              aria-label={
                showLosSectionRulesInfo
                  ? "Hide Last One Standing rules"
                  : "Show Last One Standing rules"
              }
              title={
                showLosSectionRulesInfo
                  ? "Hide Last One Standing rules"
                  : "Show Last One Standing rules"
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowLosSectionRulesInfo((prev) => !prev);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowLosSectionRulesInfo((prev) => !prev);
                }
              }}
            >
              i
            </span>
          </div>
          {showLosSectionRulesInfo && (
            <div id="los-section-rules" className="game-info-card">
              <ul className="game-rules-list">
                <li>Pick one team to win from one fixture each matchday.</li>
                <li>Picks lock 30 minutes before the first kickoff.</li>
                <li>
                  A correct winner keeps you active; a wrong pick, draw, no pick
                  after kickoff, or non-playable fixture eliminates you.
                </li>
                <li>
                  You cannot pick the same team twice within the same run.
                </li>
                <li>
                  If all the remaining players are wrong in the same matchday,
                  the league automatically resets to a new run.
                </li>
              </ul>
            </div>
          )}
          <section className="league-list">
            {displayedLosLeagues.map((league) => {
              const isExpanded = Boolean(expandedLeagueIds[league.id]);
              const isOwnedByUser = league.ownerUsername === username;
              const isDeletingLeague = Boolean(deletingLeagueById[league.id]);
              const isLeavingLeague = Boolean(leavingLeagueById[league.id]);
              const leagueLosStatus = losStatusByLeague[league.id];
              const leagueLosPick = losPickByLeague[league.id];
              const activeLosUserIds =
                leagueLosStatus?.activeUserIds ||
                leagueLosStatus?.activeUsers ||
                [];
              const activeLosUsers = leagueLosStatus?.activeUsers || [];
              const eliminatedLosUsers = leagueLosStatus?.eliminatedUsers || [];
              const usedTeamsForUser =
                leagueLosStatus?.usedTeamsByUser?.[username] || [];
              const isUserActiveInLosRun = Boolean(
                activeLosUserIds.includes(username),
              );
              const isLosFinished = Boolean(leagueLosStatus?.isFinished);
              const losFinishedReason = leagueLosStatus?.finishedReason || "";
              const losFinishedGameweekId = leagueLosStatus?.finishedGameweekId;
              const losRunLength = Number(leagueLosStatus?.runLength || 0);
              const losHasUpcomingGameweeks = Boolean(leagueLosStatus?.hasUpcomingGameweeks);
              const displayActiveLosUsers = isLosFinished ? [] : activeLosUsers;
              const isLosActiveUsersExpanded = Boolean(
                showLosActiveUsersByLeague[league.id],
              );
              const inPlayEntry = leagueLosStatus?.history?.find((e) => e.inPlay);
              const pickedByTeam = {};
              const noPickYet = [];
              if (inPlayEntry) {
                for (const result of inPlayEntry.results) {
                  if (result.pickedTeam) {
                    (pickedByTeam[result.pickedTeam] ??= []).push(result.username);
                  } else {
                    noPickYet.push(result.username);
                  }
                }
              }
              const isLosEliminatedExpanded = Boolean(
                showLosEliminatedByLeague[league.id],
              );
              const isRestartingLosRun = Boolean(
                losRestartingByLeague[league.id],
              );
              const selectedLosGameweekId =
                losSelectedGameweekByLeague[league.id] ||
                getDefaultGameweekId(sortedGameweeks);
              const selectedLosGameweek = sortedGameweeks.find(
                (gw) => gw.id === selectedLosGameweekId,
              );
              const losSelection = losSelectionByLeague[league.id] || {
                fixtureId: "",
                pickedSide: "",
              };

              return (
                <article
                  key={league.id}
                  className={`league-card ${
                    isOwnedByUser ? "league-card-owner" : "league-card-guest"
                  }`}
                >
                  <button
                    type="button"
                    className="league-header"
                    onClick={() => toggleLeague(league)}
                  >
                    <div className="league-header-main">
                      <div className="league-header-top">
                        <h3>{league.name}</h3>
                        <div className="league-meta">
                          <span
                            className={`league-badge ${
                              isOwnedByUser
                                ? "league-role-owner"
                                : "league-role-guest"
                            }`}
                          >
                            {isOwnedByUser
                              ? "Your league"
                              : `${league.ownerDisplayName || league.ownerUsername || "Owner"}'s league`}
                          </span>
                          <span className="league-badge">
                            {league.visibility}
                          </span>
                          <span className="league-badge">
                            {leagueLosStatus ? displayActiveLosUsers.length : (league.memberCount || 0)} active
                          </span>
                          <div className="league-inline-status">
                            <span
                              className={`league-badge ${
                                !leagueLosStatus
                                  ? "los-run-badge-loading"
                                  : isLosFinished && losFinishedReason === "winner" && isUserActiveInLosRun
                                    ? "los-run-badge-winner"
                                    : isUserActiveInLosRun
                                      ? "los-run-badge-active"
                                      : "los-run-badge-eliminated"
                              }`}
                            >
                              {!leagueLosStatus
                                ? "Loading..."
                                : isLosFinished && losFinishedReason === "winner" && isUserActiveInLosRun
                                  ? "Winner"
                                  : isLosFinished && losFinishedReason === "eliminated"
                                    ? "Game Over"
                                    : isUserActiveInLosRun
                                      ? "Active"
                                      : "Eliminated"}
                            </span>
                          </div>
                          {isOwnedByUser && (
                            <span
                              className={`league-delete-icon ${isDeletingLeague ? "busy" : ""}`}
                              role="button"
                              tabIndex={0}
                              title="Delete league"
                              aria-label={`Delete league ${league.name}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!isDeletingLeague) {
                                  deleteLeague(league, leagueLosStatus);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!isDeletingLeague) {
                                    deleteLeague(league, leagueLosStatus);
                                  }
                                }
                              }}
                            >
                              {isDeletingLeague ? "..." : "🗑"}
                            </span>
                          )}
                          {!isOwnedByUser && (
                            <span
                              className={`league-leave-btn ${isLeavingLeague ? "busy" : ""}`}
                              role="button"
                              tabIndex={0}
                              title="Leave league"
                              aria-label={`Leave league ${league.name}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!isLeavingLeague) leaveLeague(league);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!isLeavingLeague) leaveLeague(league);
                                }
                              }}
                            >
                              {isLeavingLeague ? "..." : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                              <polyline points="16 17 21 12 16 7"/>
                              <line x1="21" y1="12" x2="9" y2="12"/>
                            </svg>
                          )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="league-content">
                      {losErrorsByLeague[league.id] && (
                        <p className="status-message">
                          {losErrorsByLeague[league.id]}
                        </p>
                      )}

                      <div className="los-panel">
                        {leagueLosStatus && (
                          <div className="los-active-users-block">
                            <div className="los-active-users-header">
                              <p className="helper-text">
                                Run {leagueLosStatus.currentRunNumber} | Active
                                users: {displayActiveLosUsers.length}
                                {leagueLosStatus.tie
                                  ? " | Current season result: tie"
                                  : ""}
                                {isLosFinished && losFinishedGameweekId
                                  ? ` | Ended: ${losFinishedGameweekId}`
                                  : ""}
                              </p>
                              <ChevronToggleButton
                                isExpanded={isLosActiveUsersExpanded}
                                onClick={() =>
                                  setShowLosActiveUsersByLeague((prev) => ({
                                    ...prev,
                                    [league.id]: !prev[league.id],
                                  }))
                                }
                                controlsId={`los-active-users-${league.id}`}
                                expandedLabel="Hide active users list"
                                collapsedLabel="Show active users list"
                                expandedTitle="Hide active users"
                                collapsedTitle="Show active users"
                              />
                            </div>

                            {isLosActiveUsersExpanded && (
                              <div id={`los-active-users-${league.id}`}>
                                {displayActiveLosUsers.length === 0 ? (
                                  <p className="helper-text los-active-users-list">
                                    No active users left.
                                  </p>
                                ) : inPlayEntry?.gameweekStarted ? (
                                  <div className="los-picks-summary">
                                    {Object.keys(pickedByTeam).length > 0 && (
                                      <>
                                        <p className="helper-text los-section-label">Picked:</p>
                                        {Object.entries(pickedByTeam).map(([team, users]) => (
                                          <p key={team} className="helper-text los-pick-row">
                                            {team} — {users.join(", ")}
                                          </p>
                                        ))}
                                      </>
                                    )}
                                    {noPickYet.length > 0 && (
                                      <>
                                        <p className="helper-text los-section-label">No pick:</p>
                                        <p className="helper-text los-pick-row">{noPickYet.join(", ")}</p>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <p className="helper-text los-active-users-list">
                                    {displayActiveLosUsers.join(", ")}
                                  </p>
                                )}
                                {eliminatedLosUsers.length > 0 && (
                                  <div className="los-eliminated-users">
                                    <div className="los-eliminated-header">
                                      <p className="helper-text los-section-label">
                                        Eliminated:
                                      </p>
                                      <ChevronToggleButton
                                        isExpanded={isLosEliminatedExpanded}
                                        onClick={() =>
                                          setShowLosEliminatedByLeague((prev) => ({
                                            ...prev,
                                            [league.id]: !prev[league.id],
                                          }))
                                        }
                                        controlsId={`los-eliminated-${league.id}`}
                                        expandedLabel="Hide eliminated list"
                                        collapsedLabel="Show eliminated list"
                                        expandedTitle="Hide eliminated"
                                        collapsedTitle="Show eliminated"
                                      />
                                    </div>
                                    {isLosEliminatedExpanded && (
                                      <p
                                        id={`los-eliminated-${league.id}`}
                                        className="helper-text los-pick-row"
                                      >
                                        {eliminatedLosUsers.map((u) => u.username).join(", ")}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {isLosFinished && losFinishedReason === "winner" && (() => {
                          const losWinner = activeLosUsers[0];
                          return losWinner === username ? (
                            <div className="season-winner-banner">
                              <span className="season-winner-trophy">&#127942;</span>
                              <div>
                                <p className="season-winner-heading">You won the run!</p>
                                <p className="season-winner-sub">
                                  Last one standing &mdash; well played.
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="season-complete-banner">
                              <span className="season-complete-icon">&#127942;</span>
                              <span>Winner: {losWinner}</span>
                            </div>
                          );
                        })()}

                        {isLosFinished &&
                          losFinishedReason === "eliminated" && (
                            <p className="status-message lock-message">
                              Eliminated on{" "}
                              {losFinishedGameweekId || "a previous matchday"}.
                              Run length: {losRunLength} matchday
                              {losRunLength === 1 ? "" : "s"}. This LOS run is
                              finished.
                            </p>
                          )}

                        {isOwnedByUser && isLosFinished && losHasUpcomingGameweeks && (
                          <div className="submit-row">
                            <button
                              type="button"
                              className="submit-btn"
                              onClick={() => startLosNewRun(league)}
                              disabled={isRestartingLosRun}
                            >
                              {isRestartingLosRun
                                ? "Starting..."
                                : "Start New Run"}
                            </button>
                          </div>
                        )}

                        {losActionMessageByLeague[league.id] && (
                          <p className="status-message">
                            {losActionMessageByLeague[league.id]}
                          </p>
                        )}

                        <select
                          id={`los-gw-${league.id}`}
                          className="gameweek-select"
                          value={selectedLosGameweekId || ""}
                          onChange={(e) =>
                            handleLosGameweekChange(league.id, e.target.value)
                          }
                        >
                          <option value="">Choose...</option>
                          {sortedGameweeks.map((gw) => (
                            <option key={gw.id} value={gw.id}>
                              {gw.id}
                              {gw.id === currentGameweekId && hasUpcomingFixtures ? " (CURRENT)" : ""}
                            </option>
                          ))}
                        </select>

                        {leagueLosPick?.isLocked ? (
                          <p className="status-message lock-message">Picks locked</p>
                        ) : leagueLosPick?.lockTime ? (
                          <p className="status-message deadline-message">
                            Deadline:{" "}
                            {new Date(leagueLosPick.lockTime).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                            ,{" "}
                            {new Date(leagueLosPick.lockTime).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" })}
                          </p>
                        ) : null}

                        {selectedLosGameweek && (
                          <div className="los-fixture-list">
                            {selectedLosGameweek.fixtures.map((fixture) => {
                              const status = fixture.statusShort || "SCHEDULED";
                              const selectedFixture =
                                Number(losSelection.fixtureId) ===
                                Number(fixture.id);
                              const isHomeUsed = isTeamAlreadyUsed(
                                fixture.home,
                                usedTeamsForUser,
                              );
                              const isAwayUsed = isTeamAlreadyUsed(
                                fixture.away,
                                usedTeamsForUser,
                              );
                              const isLocked = Boolean(leagueLosPick?.isLocked);
                              const isSelectionDisabled =
                                isLocked ||
                                !isUserActiveInLosRun ||
                                isLosFinished;
                              return (
                                <div
                                  key={fixture.id}
                                  className="los-fixture-row"
                                >
                                  <div className="los-pick-options">
                                    <button
                                      type="button"
                                      className={`los-side-btn ${selectedFixture && losSelection.pickedSide === "HOME" ? "active" : ""} ${isHomeUsed ? "used" : ""}`}
                                      onClick={() =>
                                        handleLosSideChange(
                                          league.id,
                                          fixture.id,
                                          "HOME",
                                        )
                                      }
                                      disabled={
                                        isSelectionDisabled || isHomeUsed
                                      }
                                    >
                                      {renderTeamLabel(
                                        fixture.home,
                                        fixture.homeCrest,
                                      )}
                                      {isHomeUsed && (
                                        <span className="los-used-tag">
                                          Used
                                        </span>
                                      )}
                                    </button>
                                    <span className="los-pick-divider">vs</span>
                                    <button
                                      type="button"
                                      className={`los-side-btn ${selectedFixture && losSelection.pickedSide === "AWAY" ? "active" : ""} ${isAwayUsed ? "used" : ""}`}
                                      onClick={() =>
                                        handleLosSideChange(
                                          league.id,
                                          fixture.id,
                                          "AWAY",
                                        )
                                      }
                                      disabled={
                                        isSelectionDisabled || isAwayUsed
                                      }
                                    >
                                      {renderTeamLabel(
                                        fixture.away,
                                        fixture.awayCrest,
                                      )}
                                      {isAwayUsed && (
                                        <span className="los-used-tag">
                                          Used
                                        </span>
                                      )}
                                    </button>
                                    <span
                                      className={`fixture-status-chip ${getFixtureStatusClass(status)}`}
                                    >
                                      {getFixtureStatusLabel(status)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {leagueLosPick?.pick && (
                          <p
                            className={`los-saved-pick${
                              losFlashByLeague[league.id]
                                ? " los-pick-flash"
                                : ""
                            }`}
                          >
                            Saved pick:{" "}
                            <strong>{leagueLosPick.pick.pickedTeam}</strong> (
                            {leagueLosPick.pick.pickedSide})
                            {losFlashByLeague[league.id] && (
                              <span className="los-pick-updated">
                                {" "}
                                ✓ Updated
                              </span>
                            )}
                          </p>
                        )}

                        <div className="submit-row">
                          <button
                            type="button"
                            className="submit-btn"
                            disabled={
                              Boolean(leagueLosPick?.isLocked) ||
                              isLosFinished ||
                              !isUserActiveInLosRun ||
                              Boolean(losSavingByLeague[league.id])
                            }
                            onClick={() => submitLosPick(league)}
                          >
                            {losSavingByLeague[league.id]
                              ? "Saving..."
                              : leagueLosPick?.pick
                                ? "Update Pick"
                                : "Submit LOS Pick"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </section>
        </>
      )}

      {!hasScoreLeagues && !losLeagues.length && !isLoadingLeagues && (
        <p className="helper-text">Create or join a league to start playing.</p>
      )}
    </section>
  );
  // #endregion
}

// #region Exports: Default component export.
export default Predictions;
// #endregion
